import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Flex, Text, Radio, RadioGroup, Stack } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { saveDefaults, saveFormData, toggleIsOpen, updateFormDefinition } from '../../actions/account-settings';
import { listNormalTime, saveNormalTime } from '../../actions/normal-time';
import { Button } from '../../components/button/button';
import { ModalComponent } from '../../components/Modal/modal';
import { Select } from '../../components/select/select';
import { styles } from './account-settings.styles';
import { defaultHomepage as homepageField, defaultOrg as orgField, defaultServiceLine as serviceLineField } from '../../reducers/state/account-settings';
import theme from '../../theme';
import { WorkingHours } from '../time-entry/working-hours';
import { api } from '../../core/net';
import { getDefaultOrg, getDefaultServiceLine, getDemographicsPanelLocation, getDemographicsPanelVisibility } from '../../core/preferences/preferences';
import { AmplifyCore } from '@codametrix/ui-common';
//region FormDefinition Rule Evaluation
const evaluateSingleRule = (rule, fieldGroups, formValues)=>{
    const fieldValue = formValues[rule.field];
    switch(rule.operator){
        case 'eq':
            // For some reason the rule with int as a value was not correctly comparing to fieldValue int with ===
            // @ts-ignore
            // eslint-disable-next-line eqeqeq
            return fieldValue == rule.value;
        case 'neq':
            // For some reason the rule with int as a value was not correctly comparing to fieldValue int with ===
            // @ts-ignore
            // eslint-disable-next-line eqeqeq
            return fieldValue != rule.value;
        default:
            return false;
    }
};
const evaluateLogicalRule = (rule, fieldGroups, formValues)=>{
    if (rule.rules.length === 0) return true;
    return rule.operator === 'AND' ? rule.rules.every((r)=>evaluateRule(r, fieldGroups, formValues)) : rule.rules.some((r)=>evaluateRule(r, fieldGroups, formValues));
};
const evaluateRule = (rule, fieldGroups, formValues)=>{
    if ('operator' in rule && ('AND' === rule.operator || 'OR' === rule.operator)) {
        return evaluateLogicalRule(rule, fieldGroups, formValues);
    }
    return evaluateSingleRule(rule, fieldGroups, formValues);
};
export const runDefinitionRules = (currentField, fieldGroups, formValues)=>{
    if (!fieldGroups || fieldGroups.length === 0) return true;
    if (currentField.rules) {
        return evaluateRule(currentField.rules, fieldGroups, formValues);
    }
    return true;
};
//endregion
export const AccountSettings = ()=>{
    const classes = styles();
    const { accountDefaults: { formDefinition, formObject } } = useSelector((state)=>state.accountSettings);
    const { context } = useSelector((state)=>state.ui);
    const { savedHours } = useSelector((state)=>state.workingHours);
    const { user } = context;
    const dispatch = useDispatch();
    const activeOrg = context.activeContext;
    const activeOrgName = activeOrg?.organizationName || 'Organization';
    const timeEntry = context.activeContext ? AmplifyCore.benchEditable.isResourceAllowed(context.activeContext, 'protected_time', context.activeContext.serviceLineName ?? '') : false;
    const [hours, setHours] = useState(savedHours);
    const [activeDays, setActiveDays] = useState({
        Sunday: false,
        Monday: false,
        Tuesday: false,
        Wednesday: false,
        Thursday: false,
        Friday: false,
        Saturday: false
    });
    //region Helper Functions
    const handlePropChange = (prop, value)=>{
        const selectedOrg = context.activeContext;
        if (prop === orgField.name) {
            dispatch(saveFormData({
                defaultOrg: parseInt(value)
            }));
            dispatch(updateFormDefinition(context));
        }
        if (prop === serviceLineField.name) {
            dispatch(saveFormData({
                defaultServiceLine: parseInt(value)
            }));
        }
        if (prop === homepageField.name && selectedOrg) {
            const newHomepage = {
                path: value,
                tenantId: selectedOrg.tenantId
            };
            dispatch(saveFormData({
                defaultHomepage: newHomepage
            }));
        }
        if (prop === homepageField.name && !selectedOrg) {
            const newHomepage = {
                path: value,
                tenantId: ''
            };
            dispatch(saveFormData({
                defaultOrg: undefined,
                defaultServiceLine: undefined,
                defaultHomepage: newHomepage
            }));
        }
        if (prop === 'demographicsPanelLocation') {
            dispatch(saveFormData({
                demographicsPanelLocation: value
            }));
        }
        if (prop === 'demographicsPanelVisibility') {
            dispatch(saveFormData({
                demographicsPanelVisibility: value
            }));
        }
    };
    const handleClose = ()=>{
        dispatch(toggleIsOpen(false));
    };
    const handleSaveAccountDefaults = ()=>{
        if (user.id) {
            const accountDefaultsPayload = {
                userId: user.id,
                defaultOrg: formObject.defaultOrg,
                defaultServiceLine: formObject.defaultServiceLine,
                demographicsPanelLocation: formObject.demographicsPanelLocation,
                demographicsPanelVisibility: formObject.demographicsPanelVisibility
            };
            dispatch(saveDefaults(accountDefaultsPayload));
        }
    };
    const handleSaveOrgDefaults = ()=>{
        if (user.id && activeOrg) {
            const orgDefaultsPayload = {
                userId: user.id,
                defaultHomepage: formObject.defaultHomepage
            };
            dispatch(saveDefaults(orgDefaultsPayload));
        }
    };
    const handleSaveWorkingHours = ()=>{
        if (timeEntry) {
            let activeHours = {};
            Object.keys(hours).forEach((day)=>{
                if (activeDays[day] || hours[day] >= 0 && hours[day] <= 24) {
                    activeHours[day] = hours[day];
                } else {
                    activeHours[day] = 0;
                }
            });
            dispatch(saveNormalTime({
                hours: activeHours,
                user,
                context
            }));
        }
    };
    const handleSave = ()=>{
        handleSaveAccountDefaults();
        if (activeOrg) {
            handleSaveOrgDefaults();
        }
        if (timeEntry) {
            handleSaveWorkingHours();
        }
    };
    const onOptionChange = (event)=>{
        const target = event.target;
        const { name, value } = target;
        handlePropChange(name, value);
    };
    const optionConverter = (field)=>{
        const { options, labelField, valueField } = field;
        let opts = [];
        options?.map((item)=>{
            opts?.push({
                label: item[labelField],
                value: item[valueField]
            });
            return {};
        });
        return opts;
    };
    const getSelectedValue = (field, key)=>{
        const options = field?.options;
        if (!options) {
            return '';
        }
        if (options.length === 1) {
            return options[0][field.labelField];
        }
        const res = options?.filter((item)=>String(item[field.valueField]) === key);
        return res && res?.[0]?.[field.labelField];
    };
    const getFieldValue = (field)=>{
        switch(field.key){
            case 'defaultOrg':
                return formObject.defaultOrg?.toString() || '';
            case 'defaultServiceLine':
                return formObject.defaultServiceLine?.toString() || '';
            case 'defaultHomepage':
                return formObject.defaultHomepage?.path || '';
            case 'demographicsPanelLocation':
                return formObject.demographicsPanelLocation || 'right';
            case 'demographicsPanelVisibility':
                return formObject.demographicsPanelVisibility || 'open';
            default:
                return '';
        }
    };
    //endregion
    //region Lifecycle
    useEffect(()=>{
        const initializeComponent = async ()=>{
            // Update form definition first to ensure we have the correct structure
            dispatch(updateFormDefinition(context));
            // Load working hours if timeEntry is enabled
            if (timeEntry && user.userId && context.activeContext?.tenantId) {
                dispatch(listNormalTime({
                    dateToday: new Date(),
                    userId: user.userId,
                    tenantId: context.activeContext?.tenantId
                }));
            }
            // Fetch user preferences
            try {
                if (context.user?.id) {
                    const userDefaults = await api({
                        endpoint: `/userpreferences/${context.user.id}/v1`
                    });
                    // Extract preference values
                    const defaultOrgId = getDefaultOrg(userDefaults) ?? -1;
                    const defaultServiceLine = getDefaultServiceLine(userDefaults) ?? -1;
                    const demographicsPanelLocation = getDemographicsPanelLocation(userDefaults) ?? 'right';
                    const demographicsPanelVisibility = getDemographicsPanelVisibility(userDefaults) ?? 'open';
                    // Set default homepage
                    const defaultHomepage = formObject.defaultHomepage || {
                        path: 'None',
                        tenantId: context.activeContext?.tenantId || ''
                    };
                    dispatch(saveFormData({
                        defaultOrg: defaultOrgId,
                        defaultServiceLine: defaultServiceLine,
                        defaultHomepage,
                        demographicsPanelLocation,
                        demographicsPanelVisibility
                    }));
                }
            } catch (error) {
                console.error('Error initializing account settings:', error);
                // Set fallback values if preferences fetch fails
                const defaultOrgId = context.activeContext?.organizationId || -1;
                const defaultServiceLineId = -1;
                dispatch(saveFormData({
                    defaultOrg: defaultOrgId,
                    defaultServiceLine: defaultServiceLineId,
                    defaultHomepage: formObject.defaultHomepage || {
                        path: 'None',
                        tenantId: context.activeContext?.tenantId || ''
                    },
                    demographicsPanelLocation: 'right',
                    demographicsPanelVisibility: 'open'
                }));
            }
        };
        initializeComponent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    //endregion
    //region Components
    const renderSaveButton = (label, onClick)=>/*#__PURE__*/ _jsx(Button, {
            dataTestId: `save${label.replace(/\s+/g, '')}Btn`,
            label: `Save ${label}`,
            onClick: onClick
        });
    const renderFormGroups = ()=>{
        if (!formDefinition.fieldGroups) return null;
        let fieldGroupsToRender = [
            ...formDefinition.fieldGroups
        ];
        return fieldGroupsToRender.map((item, index)=>{
            const groupLabel = index === 1 && item.label !== 'Account Defaults' ? `${activeOrgName} Defaults` : item.label || '';
            return /*#__PURE__*/ _jsxs("fieldset", {
                style: classes.fieldset,
                children: [
                    /*#__PURE__*/ _jsx("legend", {
                        style: classes.legend,
                        children: groupLabel
                    }),
                    /*#__PURE__*/ _jsxs(Flex, {
                        flexDirection: 'column',
                        rowGap: 5,
                        padding: theme.space[4] + ' ' + theme.space[16] + ' ' + theme.space[16] + ' ' + theme.space[16],
                        children: [
                            item.fields.map((field, fieldIndex)=>{
                                // Handle multiField type
                                if (field.type === 'multiField') {
                                    return /*#__PURE__*/ _jsxs(Flex, {
                                        flexDirection: 'row',
                                        columnGap: 5,
                                        width: '66%',
                                        alignItems: 'flex-start',
                                        children: [
                                            /*#__PURE__*/ _jsx(Text, {
                                                role: "textbox",
                                                minWidth: '180px',
                                                width: '30%',
                                                paddingBottom: 5,
                                                children: field.label
                                            }),
                                            /*#__PURE__*/ _jsxs(Flex, {
                                                flexDirection: 'column',
                                                width: '100%',
                                                children: [
                                                    field.fields?.map((subField)=>/*#__PURE__*/ _jsxs(Flex, {
                                                            alignItems: 'center',
                                                            marginBottom: subField === field.fields[field.fields.length - 1] ? 0 : 2,
                                                            children: [
                                                                /*#__PURE__*/ _jsxs(Text, {
                                                                    role: "textbox",
                                                                    width: '80px',
                                                                    fontSize: 'sm',
                                                                    children: [
                                                                        subField.label,
                                                                        ":"
                                                                    ]
                                                                }),
                                                                subField.type === 'radio' && /*#__PURE__*/ _jsx(RadioGroup, {
                                                                    onChange: (value)=>handlePropChange(subField.name, value),
                                                                    value: getFieldValue(subField),
                                                                    name: subField.name,
                                                                    children: /*#__PURE__*/ _jsx(Stack, {
                                                                        direction: "row",
                                                                        spacing: 4,
                                                                        children: subField.options?.map((option)=>/*#__PURE__*/ _jsx(Radio, {
                                                                                value: option[subField.valueField || 'value'],
                                                                                "data-testid": `${subField.name}-${option[subField.valueField || 'value']}`,
                                                                                children: option[subField.labelField || 'label']
                                                                            }, option[subField.valueField || 'value']))
                                                                    })
                                                                })
                                                            ]
                                                        }, subField.key)),
                                                    field.helpText && /*#__PURE__*/ _jsx(Text, {
                                                        role: "textbox",
                                                        sx: classes.label,
                                                        children: field.helpText
                                                    })
                                                ]
                                            })
                                        ]
                                    }, field.name);
                                }
                                // Regular field rendering
                                return /*#__PURE__*/ _jsxs(Flex, {
                                    flexDirection: 'row',
                                    columnGap: 5,
                                    width: '66%',
                                    alignItems: 'center',
                                    children: [
                                        /*#__PURE__*/ _jsx(Text, {
                                            role: "textbox",
                                            width: '25%',
                                            paddingBottom: 5,
                                            children: field.label
                                        }),
                                        /*#__PURE__*/ _jsx(Flex, {
                                            flexDirection: 'column',
                                            width: '100%',
                                            children: /*#__PURE__*/ _jsxs("div", {
                                                className: "form-group",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Select, {
                                                        items: optionConverter(field),
                                                        name: field.name,
                                                        dataTestId: field.name,
                                                        value: getSelectedValue(field, getFieldValue(field)),
                                                        onChange: onOptionChange
                                                    }),
                                                    field.helpText && /*#__PURE__*/ _jsx(Text, {
                                                        role: "textbox",
                                                        sx: classes.label,
                                                        children: field.helpText
                                                    })
                                                ]
                                            })
                                        })
                                    ]
                                }, field.name);
                            }),
                            /*#__PURE__*/ _jsxs(Flex, {
                                justifyContent: "flex-end",
                                marginTop: 3,
                                children: [
                                    item.label === 'Account Defaults' && renderSaveButton('Account Defaults', handleSaveAccountDefaults),
                                    item.label !== 'Account Defaults' && index === 1 && renderSaveButton(`${activeOrgName} Defaults`, handleSaveOrgDefaults)
                                ]
                            })
                        ]
                    })
                ]
            }, index);
        });
    };
    //endregion
    return /*#__PURE__*/ _jsx(ModalComponent, {
        size: 'xl',
        width: '52rem',
        isOpen: true,
        handleClose: handleClose,
        showCloseIcon: true,
        modalHeader: /*#__PURE__*/ _jsx("div", {
            children: "Account Settings"
        }),
        modalContent: /*#__PURE__*/ _jsxs(Flex, {
            flexDirection: 'column',
            children: [
                renderFormGroups(),
                timeEntry && /*#__PURE__*/ _jsxs("fieldset", {
                    style: classes.fieldset,
                    children: [
                        /*#__PURE__*/ _jsx("legend", {
                            style: classes.legend,
                            children: "Working Hours"
                        }),
                        /*#__PURE__*/ _jsx(WorkingHours, {
                            hours: hours,
                            activeDays: activeDays,
                            setHours: setHours,
                            setActiveDays: setActiveDays
                        }),
                        /*#__PURE__*/ _jsx(Flex, {
                            justifyContent: "flex-end",
                            margin: 5,
                            children: renderSaveButton('Working Hours', handleSaveWorkingHours)
                        })
                    ]
                })
            ]
        }),
        modalFooter: /*#__PURE__*/ _jsx(_Fragment, {
            children: /*#__PURE__*/ _jsx(Button, {
                dataTestId: "saveAllBtn",
                label: 'Save All',
                onClick: handleSave
            })
        })
    });
};
